import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AdminBoostEarnings = ({ musicianId, currentEarnings, onBoostSuccess }) => {
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [boostAmount, setBoostAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('add'); // 'add' or 'subtract'

  // Helper function to clean and parse earnings values
  const parseEarnings = (value) => {
    if (!value) return 0;
    // Remove currency symbols and parse as number
    const cleaned = value.toString().replace(/[$,]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  const handleBoost = async () => {
    if (!boostAmount || isNaN(boostAmount) || parseFloat(boostAmount) <= 0) {
      alert('Please enter a valid positive amount');
      return;
    }

    // Calculate final amount based on adjustment type
    const finalAmount = adjustmentType === 'subtract' ? -parseFloat(boostAmount) : parseFloat(boostAmount);
    const actionWord = adjustmentType === 'subtract' ? 'deduct' : 'boost';

    setLoading(true);
    try {
      const functions = getFunctions();
      const adminBoostAccount = httpsCallable(functions, 'adminBoostAccount');
      const result = await adminBoostAccount({
        userId: musicianId,
        amount: finalAmount, // This can be negative for deductions
        reason: reason || `Admin manual ${actionWord}`
      });

      // Remove success popup - just refresh data
      setShowBoostModal(false);
      setBoostAmount('');
      setReason('');
      setAdjustmentType('add');
      
      // Notify parent component to refresh data
      if (onBoostSuccess) {
        onBoostSuccess();
      }
    } catch (error) {
      console.error(`${actionWord} error:`, error);
      // Keep error popup for debugging
      alert(`❌ ${actionWord} failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Main Boost Button */}
      <button
        onClick={() => setShowBoostModal(true)}
        disabled={loading}
        style={{
          backgroundColor: '#28a745',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          padding: '8px 16px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
          marginTop: '10px',
          width: '100%'
        }}
      >
        💰 Adjust Earnings
      </button>

      {/* Enhanced Boost Modal */}
      {showBoostModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#2c3e50',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            color: 'white'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#f8f9fa' }}>
              💰 Adjust Account Earnings
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#ced4da' }}>
                Current Balance: <strong>${parseEarnings(currentEarnings).toFixed(2)}</strong>
              </p>
            </div>

            {/* Adjustment Type Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Adjustment Type:
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setAdjustmentType('add')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: adjustmentType === 'add' ? '#28a745' : '#495057',
                    color: 'white',
                    border: adjustmentType === 'add' ? '2px solid #34ce57' : '1px solid #6c757d',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ➕ Add Money
                </button>
                <button
                  onClick={() => setAdjustmentType('subtract')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: adjustmentType === 'subtract' ? '#dc3545' : '#495057',
                    color: 'white',
                    border: adjustmentType === 'subtract' ? '2px solid #e55353' : '1px solid #6c757d',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  ➖ Deduct Money
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Amount ($):
              </label>
              <input
                type="number"
                value={boostAmount}
                onChange={(e) => setBoostAmount(e.target.value)}
                placeholder="Enter positive amount (e.g. 20)"
                min="0.01"
                step="0.01"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #495057',
                  backgroundColor: '#495057',
                  color: 'white',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '12px', 
                color: adjustmentType === 'add' ? '#28a745' : '#dc3545' 
              }}>
                {adjustmentType === 'add' ? '➕' : '➖'} Enter positive amount only. 
                Will {adjustmentType === 'add' ? 'add to' : 'subtract from'} balance.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Reason (optional):
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`e.g. ${adjustmentType === 'add' ? 'Performance bonus' : 'Refund processed'}`}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #495057',
                  backgroundColor: '#495057',
                  color: 'white',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowBoostModal(false);
                  setAdjustmentType('add');
                  setBoostAmount('');
                  setReason('');
                }}
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleBoost}
                disabled={loading || !boostAmount}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: loading || !boostAmount ? '#6c757d' : 
                    adjustmentType === 'add' ? '#28a745' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading || !boostAmount ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Processing...' : 
                 adjustmentType === 'add' ? '➕ Add Money' : '➖ Deduct Money'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBoostEarnings;